import { useContext, useEffect } from "react";
import { removeTimer, setIdTimer } from "t-lib-ui/helpers";
import { EmbedService, RestPreferences, UserPreferences } from "../schema-copy/api-schema";
import { AppState } from "../context/general";
import { usePutPreferences } from "./api";
import { getJsonStore } from "./json-store";

const TIMER_ID = 'preferences-timer';
const SAVE_INTERVAL = 3000;
// localStorage key holding the full preferences blob for an anonymous (no-login) session.
const ANON_PREFS_KEY = 'anon-preferences';

let preferenceUserId: string|undefined;
// True when there is no logged-in user: preferences live entirely in localStorage and are never
// synced to the server.
let anonMode = false;

// Local edits that have not yet been confirmed saved to the server. On load these
// are overlaid on top of the server preferences, so changes made on other devices
// are picked up for every field except those with an unsynced local edit. This
// replaces the old "newest whole-blob wins" logic (which compared a client clock
// against the server clock and discarded every field from the other device).
let pendingPreferences: Partial<UserPreferences> = {};

// flush method (set by usePreferences) so logout / tab-hide can force an immediate save
let flushHook: (() => Promise<void>)|undefined;

// Set by usePreferences so non-React callers (the feed engine on unsubscribe) can drop
// a feed's custom display name without holding a preferences hook themselves.
let removeCustomFeedNameHook: ((feedId: string) => void)|undefined;

// Set by usePreferences so the feed engine can push fresh server-side preferences
// (returned by GET /api/feeds) into app state without holding a preferences hook.
let applyServerPreferencesHook: ((prefs: UserPreferences) => void)|undefined;

// Set by usePreferences so embed consent overlays (imperative DOM code in the post
// pipeline) can persist a per-service consent without holding a preferences hook.
let grantEmbedConsentHook: ((service: EmbedService) => void)|undefined;

// Record that the user allowed embeds of the given third-party service.
export function grantEmbedConsent(service: EmbedService): void {
    grantEmbedConsentHook?.(service);
}

// Remove a feed's custom display name. Called when the feed is unsubscribed/deleted so
// stale overrides don't linger in the user's saved preferences. No-op if no name is set.
export function removeCustomFeedName(feedId: string): void {
    removeCustomFeedNameHook?.(feedId);
}

/** Apply fresh server-side preferences (e.g. returned by GET /api/feeds on startup or
 *  reconnect) to app state. Locally-pending unsynced edits stay layered on top so a
 *  change the user is still typing isn't clobbered by the server's older value. No-op
 *  if no preferences hook has been registered yet (called before App mounts). */
export function applyServerPreferences(prefs: UserPreferences): void {
    applyServerPreferencesHook?.(prefs);
}

// Key in the user's JsonStore (stored as `<userId>-pending-preferences`). Always persisted, not only
// with offline reading on: an edit must survive a reload before its debounced save fires.
const PENDING_KEY = 'pending-preferences';
// Where pending edits were kept before the JsonStore; read once so an upgrade loses nothing.
const legacyPendingKey = (userId: string) => 'pref-pending-' + userId;

function loadPending(userId: string): Partial<UserPreferences> {
    const store = getJsonStore(userId);
    const pending = store.getJson<Partial<UserPreferences>>(PENDING_KEY, {});
    try {
        const legacy = localStorage.getItem(legacyPendingKey(userId));
        if (!legacy) return pending;
        localStorage.removeItem(legacyPendingKey(userId));
        const merged = {...JSON.parse(legacy), ...pending};
        store.setJson(PENDING_KEY, merged);
        return merged;
    }
    catch(e) {
        console.error('Error migrating pending preferences', e);
        return pending;
    }
}

function persistPending() {
    if (!preferenceUserId)
        return;
    const store = getJsonStore(preferenceUserId);
    if (Object.keys(pendingPreferences).length)
        store.setJson(PENDING_KEY, pendingPreferences);
    else
        store.remove(PENDING_KEY);
}

// Load initial preferences when app starts. The server copy is authoritative;
// any locally-pending (unsynced) edits are overlaid on top so they aren't lost.
export function loadInitialPreferences(loadedPreferences?: RestPreferences): UserPreferences {
    removeTimer(TIMER_ID);
    flushHook = undefined;

    if (!loadedPreferences) {
        // Anonymous session: load the full blob from localStorage; mutations persist there, never
        // to the server.
        anonMode = true;
        pendingPreferences = {};
        preferenceUserId = undefined;
        try {
            return JSON.parse(localStorage.getItem(ANON_PREFS_KEY) ?? '{}') as UserPreferences;
        } catch (e) {
            console.error('Error loading anon preferences from localStorage', e);
            return {};
        }
    }

    anonMode = false;
    const {id, preferences} = loadedPreferences;
    preferenceUserId = id;
    pendingPreferences = loadPending(id);

    return {...(preferences ?? {}), ...pendingPreferences};
}

export function logOutPreferences() {
    removeTimer(TIMER_ID);
    const hook = flushHook;
    flushHook = undefined;
    // best-effort flush of anything still pending before we drop local state
    if (hook)
        hook().catch(e => console.error('Error flushing preferences on logout', e));

    if (preferenceUserId)
        getJsonStore(preferenceUserId).remove(PENDING_KEY);
    pendingPreferences = {};
    preferenceUserId = undefined;
}

// Force immediate save of pending preferences (e.g. before subscribing to a feed,
// or when the tab is hidden on mobile so the debounced write isn't lost).
export async function flushPendingPreferences(): Promise<void> {
    removeTimer(TIMER_ID);
    if (flushHook)
        await flushHook();
}

export function usePreferences(): [UserPreferences, (newPrefs: Partial<UserPreferences>) => void] {
    const appState = useContext(AppState);
    const putPreferences = usePutPreferences();

    async function flush() {
        // Anonymous preferences are persisted to localStorage on every change (see
        // updatePreferences); there is nothing to POST.
        if (anonMode) return;
        if (!Object.keys(pendingPreferences).length)
            return;
        // Send only the changed keys; the server merges them per-field.
        const sent = {...pendingPreferences};
        await putPreferences(sent);
        // Clear only the keys we sent that haven't been changed again since.
        for (const key of Object.keys(sent) as (keyof UserPreferences)[])
            if (JSON.stringify(pendingPreferences[key]) == JSON.stringify(sent[key]))
                delete pendingPreferences[key];
        persistPending();
    }

    flushHook = flush;

    // Push edits that were left pending from a previous session (e.g. a debounced
    // save that never fired because the tab was closed) once we're mounted again.
    useEffect(() => {
        if (Object.keys(pendingPreferences).length)
            setIdTimer(TIMER_ID, SAVE_INTERVAL, () => flush().catch(e => console.error('Error saving preferences', e)));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Drop keys whose value equals the current in-memory value, so the PUT body
    // only carries fields that actually changed. JSON.stringify is the cheap deep
    // compare we need for objects like customFeedNames.
    const updatePreferences = (newPrefs: Partial<UserPreferences>) => {
        const current = appState.state.userPreferences;
        const changed: Partial<UserPreferences> = {};
        for (const key of Object.keys(newPrefs) as (keyof UserPreferences)[])
            if (JSON.stringify(newPrefs[key]) != JSON.stringify(current[key]))
                (changed as any)[key] = newPrefs[key];
        if (!Object.keys(changed).length) return;
        const merged = {...current, ...changed};
        appState.setUserPreferences(merged);
        if (anonMode) {
            // No server sync: persist the whole blob to localStorage.
            try { localStorage.setItem(ANON_PREFS_KEY, JSON.stringify(merged)); }
            catch (e) { console.error('Error saving anon preferences', e); }
            return;
        }
        pendingPreferences = {...pendingPreferences, ...changed};
        persistPending();
        setIdTimer(TIMER_ID, SAVE_INTERVAL, () => flush().catch(e => console.error('Error saving preferences', e)));
    };

    // Send the full reduced map (an empty object when the last name is removed) rather
    // than `undefined`: JSON.stringify drops undefined values, so the server-side merge
    // would otherwise keep the stale entry.
    removeCustomFeedNameHook = (feedId: string) => {
        const current = appState.state.userPreferences.customFeedNames;
        if (!current || !(feedId in current)) return;
        const next = {...current};
        delete next[feedId];
        updatePreferences({ customFeedNames: next });
    };

    grantEmbedConsentHook = (service: EmbedService) => {
        const current = appState.state.userPreferences.embedConsent;
        if (current?.[service]) return;
        updatePreferences({ embedConsent: {...(current ?? {}), [service]: true} });
    };

    // Server prefs are authoritative for any key the user hasn't locally edited since the
    // last successful flush; pendingPreferences (= unsynced local edits) overlay them so
    // an in-flight rename isn't clobbered by an older copy from the server.
    applyServerPreferencesHook = (prefs: UserPreferences) => {
        appState.setUserPreferences({...prefs, ...pendingPreferences});
    };

    return [appState.state.userPreferences, updatePreferences];
}
